import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import ApiService from "../../../api/ApiServices";
import { setUserData } from "../../../redux/rootSlice";
import { AppPaths } from "../../../utils/AppPaths";
import PrimaryButton from "../../../components/PrimaryButton";
import Toggle from "../../../components/Toggle";
import AlertModal from "../../../components/AlertModal";

const EditnotificationSettings = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userData = useAppSelector<any>((state) => state.root.userData);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertTitle, setAlertTitle] = useState<string>("Oops!");
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [shouldNavigateProfile, setShouldNavigateProfile] = useState<boolean>(false);

  const [preferences, setPreferences] = useState({
    dailyChallenge: false,
    dailyCheckIn: false,
    newMessages: false,
    housePoints: false,
    importantNotices: false,
  });

  const openAlert = (title: string, message: string) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setShowAlert(true);
  };

  const fetchUserProfile = async () => {
    try {
      const response = await ApiService.getUserProfile();
      const user = response?.data?.user ?? response?.data ?? null;
      if (user) {
        dispatch(setUserData(user));
        if (user.notificationSettings) {
          setPreferences(user.notificationSettings);
        }
      }
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
      setShouldNavigateProfile(false);
      openAlert("Error", "Unable to load user data");
    }
  };

  useEffect(() => {
    if (userData?.notificationSettings) {
      setPreferences(userData.notificationSettings);
    } else if (!userData) {
      fetchUserProfile();
    }
  }, [userData]);

  const handleToggle = (key: keyof typeof preferences) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const onSave = async () => {
    try {
      const response = await ApiService.updateUserDetails({
        notificationSettings: preferences,
      });
      if (response?.status) {
        setShouldNavigateProfile(true);
        openAlert("Success", "Preferences updated successfully");
        dispatch(setUserData(response.data));
      }
    } catch (error) {
      console.error("Error while updating preferences:", error);
      setShouldNavigateProfile(false);
      openAlert("Error", "Failed to update preferences");
    }
  };

  const PreferenceItem = ({
    label,
    description,
    value,
    onToggle,
    icon,
  }: {
    label: string;
    description?: string;
    value: boolean;
    onToggle: () => void;
    icon: string;
  }) => (
    <div className="flex flex-row items-center justify-between py-4 border-b border-gray-100 last:border-0">
      <div className="flex flex-row items-center">
        <img src={icon} alt="" className="w-6 h-6 mr-3" />
        <div className="flex flex-col">
          <span className="inter-16-medium text-gray-900">{label}</span>
          {description && <span className="inter-12-regular text-gray-400">{description}</span>}
        </div>
      </div>
      <Toggle checked={value} onChange={onToggle} color="#E91E63" />
    </div>
  );

  return (
    <div className="flex flex-1 flex-col relative pb-20 mx-1 pt-3 mb-5">
      <div className="flex flex-row mx-3 items-center">
        <div>
          <img
            onClick={() => {
              navigate(-1);
            }}
            alt=""
            className="home-frag-pic-backbtn mr-2"
            src={require("../../../assets/arrow-left.png")}
          />
        </div>
        <div className="btn-onb2-text flex flex-1 items-center justify-center">
          Notification Preferences
        </div>
      </div>

      <div className="mx-4 mt-4">
        <h3 className="inter-14-bold mb-4">Activity & Updates</h3>

        <div className="my-journey-card p-4">
          <PreferenceItem
            icon={require("../../../assets/calendar-icon.png")}
            label="Daily Challenge Reminders"
            value={preferences.dailyChallenge}
            onToggle={() => handleToggle("dailyChallenge")}
          />
          <PreferenceItem
            icon={require("../../../assets/check-icon.png")}
            label="Daily Check-in Reminders"
            value={preferences.dailyCheckIn}
            onToggle={() => handleToggle("dailyCheckIn")}
          />
          <PreferenceItem
            icon={require("../../../assets/message-icon.png")}
            label="newMessages"
            description="Notifies you when you get a new DM"
            value={preferences.newMessages}
            onToggle={() => handleToggle("newMessages")}
          />
          <PreferenceItem
            icon={require("../../../assets/medal-icon.png")}
            label="House Points updates"
            value={preferences.housePoints}
            onToggle={() => handleToggle("housePoints")}
          />
          <PreferenceItem
            icon={require("../../../assets/info-icon.png")}
            label="Important notices"
            value={preferences.importantNotices}
            onToggle={() => handleToggle("importantNotices")}
          />
        </div>
      </div>

      <PrimaryButton
        onClick={onSave}
        text="SAVE"
        containerStyles="py-3 mx-5 mt-10"
        textStyle="inter-16-bold"
      />
      <AlertModal
        visible={showAlert}
        title={alertTitle}
        message={alertMessage}
        onConfirm={() => {
          setShowAlert(false);
          if (shouldNavigateProfile && alertTitle === "Success") {
            setShouldNavigateProfile(false);
            navigate(AppPaths.PROFILE);
          }
        }}
      />
    </div>
  );
};

export default EditnotificationSettings;